// frontend/packages/business-console/src/components/content/CopilotBar.jsx
//
// In-editor CMS copilot: the operator types (or taps) an instruction ("make the
// intro punchier", "rewrite in our brand voice", "fix on-page SEO") and the
// assistant proposes a change PER FIELD on the doc being edited. It NEVER writes
// directly - it shows the proposals and the operator taps Apply per field (or
// Apply all), which writes through the same frontmatter/body setters + draft
// path the manual editor uses (D30: the AI proposes, the client approves, one
// click, never hand-edits). Same pattern as SeoAssistCard, generalized.
//
// The backend (POST /v2/content/revise) does the one bounded, metered, em-dash-
// stripped LLM call and filters output to the fields we sent. This is pure UI +
// one fetch; it degrades open (shows "couldn't revise").

import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import { useTokens, Surface } from '../primitives';
import { reviseContent } from '../../utils/contentApi';
import { collectProseFields } from '../../utils/contentEditorHelpers';

// Quick prompts. Keep them short + benefit-framed; no em/en-dashes (house
// style). Tapping one runs it immediately.
const PRESETS = [
  'Make it punchier',
  'Rewrite in our brand voice',
  'Tighten this up',
  'Improve for SEO',
  'Fix grammar and clarity',
];

// Human label for a field key (falls back to the key).
function fieldLabel(field) {
  if (field === 'body') return 'Page content';
  const s = String(field || '');
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/([A-Z])/g, ' $1');
}

// The editable prose on this doc, body first. Two things this deliberately
// does NOT do, both of which it used to:
//   - send every string frontmatter value, including `cld:` media tokens,
//     slugs, dates and URLs, which the model would then happily "improve"
//     into a value that breaks the page the moment someone taps Apply all;
//   - append the body LAST, which meant a page with a dozen frontmatter
//     strings pushed it past the backend's field cap and the copilot could
//     not touch the page content at all.
// The rules live in contentEditorHelpers so SEO assist agrees with us about
// what counts as copy. The cap matches contentReviseService's MAX_FIELDS.
const MAX_REVISE_FIELDS = 12;

function ChangeRow({ change, current, applied, onApply, t }) {
  const preview = String(change.value || '').slice(0, 240);
  return (
    <Box
      sx={{
        borderTop: `1px solid ${t.cardBorder}`,
        pt: 1.5,
        mt: 1.5,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
          {fieldLabel(change.field)}
        </Typography>
        <Button
          size="small"
          variant={applied ? 'text' : 'contained'}
          disabled={applied || String(change.value) === String(current || '')}
          startIcon={applied ? <CheckRoundedIcon fontSize="small" /> : null}
          onClick={() => onApply(change)}
        >
          {applied ? 'Applied' : 'Apply'}
        </Button>
      </Stack>
      <Typography
        sx={{
          fontSize: 13,
          color: t.textSecondary,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {preview}
        {change.value.length > 240 ? '…' : ''}
      </Typography>
    </Box>
  );
}

export default function CopilotBar({
  frontmatterObj,
  markdownBody,
  onApplyField,
  onApplyBody,
}) {
  const t = useTokens();
  const [instruction, setInstruction] = useState('');
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(null);
  const [applied, setApplied] = useState({});
  const [error, setError] = useState('');

  const sentFields = collectProseFields(
    frontmatterObj,
    markdownBody,
    MAX_REVISE_FIELDS
  );

  async function run(text) {
    const instr = String(text ?? instruction).trim();
    if (!instr || loading) return;
    setLoading(true);
    setError('');
    setRevision(null);
    setApplied({});
    try {
      const result = await reviseContent({
        instruction: instr,
        fields: sentFields,
      });
      if (!result || !result.changes || result.changes.length === 0) {
        setError(
          "The assistant couldn't find a change to make. Try rephrasing."
        );
      } else {
        setRevision(result);
      }
    } catch (e) {
      setError(e?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function applyChange(change) {
    if (change.field === 'body') onApplyBody(change.value);
    else onApplyField(change.field, change.value);
    setApplied((prev) => ({ ...prev, [change.field]: true }));
  }

  function applyAll() {
    (revision?.changes || []).forEach(applyChange);
  }

  const allApplied =
    revision &&
    revision.changes.length > 0 &&
    revision.changes.every((c) => applied[c.field]);

  return (
    <Surface sx={{ p: 2, borderLeft: '3px solid', borderLeftColor: t.purple }}>
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.5 }}>
        <AutoAwesomeOutlinedIcon fontSize="small" sx={{ color: t.purple }} />
        <Typography sx={{ fontWeight: 900, fontSize: 14 }}>
          Edit this page with AI
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 12.5, color: t.muted, mb: 1.25 }}>
        Just ask. The assistant writes or reworks any part of this page for you,
        proposes the change, and you approve it with one click. You do not need
        to fill out the fields below yourself.
      </Typography>

      <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mb: 1.25 }}>
        {PRESETS.map((p) => (
          <Chip
            key={p}
            label={p}
            size="small"
            variant="outlined"
            disabled={loading}
            onClick={() => run(p)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Stack>

      <Stack direction="row" gap={1} alignItems="flex-end">
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder="Ask AI to write or change this page. Example: rewrite the intro to be punchier, or add a section about pricing."
          value={instruction}
          disabled={loading}
          onChange={(e) => setInstruction(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) run();
          }}
        />
        <IconButton
          color="primary"
          disabled={loading || !instruction.trim()}
          onClick={() => run()}
          aria-label="Ask the copilot"
        >
          {loading ? <CircularProgress size={18} /> : <SendRoundedIcon />}
        </IconButton>
      </Stack>

      {error ? (
        <Typography sx={{ fontSize: 12.5, color: t.red, mt: 1 }}>
          {error}
        </Typography>
      ) : null}

      {revision ? (
        <Box sx={{ mt: 1.5 }}>
          {revision.summary ? (
            <Typography sx={{ fontSize: 13, color: t.textSecondary, mb: 0.5 }}>
              {revision.summary}
            </Typography>
          ) : null}
          {revision.changes.map((c) => (
            <ChangeRow
              key={c.field}
              change={c}
              current={sentFields[c.field]}
              applied={!!applied[c.field]}
              onApply={applyChange}
              t={t}
            />
          ))}
          <Stack direction="row" gap={1} sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              disabled={allApplied}
              startIcon={
                allApplied ? <CheckRoundedIcon fontSize="small" /> : null
              }
              onClick={applyAll}
            >
              {allApplied ? 'All applied' : 'Apply all'}
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setRevision(null)}
            >
              Dismiss
            </Button>
          </Stack>
        </Box>
      ) : null}
    </Surface>
  );
}
